/**
 * Webhook Configuration API Layer
 *
 * Per Spec 0017 Phase 5 + Design 5.3.
 * BFF routes: /webhooks/config/*
 *
 * These API Gateway resources sit at /webhooks/config (not /v1/admin/).
 * CloudFront routes /v1/* to API GW without originPath, so the browser
 * calls /v1/webhooks/config which API GW interprets as stage /v1 +
 * resource /webhooks/config.
 */

import { get, put, post, del } from './client';
import type {
  WebhookConfig,
  WebhookTestResult,
  SetWebhookRequest,
  WebhookConfigListResponse,
} from '@/types/webhook-config';

/**
 * List all webhook configurations with optional filters.
 */
export async function listWebhookConfigs(params?: {
  enabled?: boolean;
  limit?: number;
  cursor?: string;
}): Promise<WebhookConfigListResponse> {
  const queryParams: Record<string, string> = {};

  if (params?.enabled !== undefined) queryParams.enabled = String(params.enabled);
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.cursor) queryParams.cursor = params.cursor;

  return get<WebhookConfigListResponse>('/webhooks/config', queryParams);
}

/**
 * Get webhook config for a specific merchant.
 */
export async function getWebhookConfig(merchantId: string): Promise<WebhookConfig> {
  return get<WebhookConfig>(`/webhooks/config/${encodeURIComponent(merchantId)}`);
}

/**
 * Set or update webhook config for a merchant.
 * Returns 201 with cleartext secret on first create, 200 with masked on update.
 */
export async function setWebhookConfig(
  merchantId: string,
  data: SetWebhookRequest
): Promise<WebhookConfig> {
  return put<WebhookConfig>(
    `/webhooks/config/${encodeURIComponent(merchantId)}`,
    data
  );
}

/**
 * Deactivate (soft-delete) webhook config.
 */
export async function deleteWebhookConfig(merchantId: string): Promise<WebhookConfig> {
  return del<WebhookConfig>(`/webhooks/config/${encodeURIComponent(merchantId)}`);
}

/**
 * Rotate HMAC secret. Old secret immediately invalidated.
 * Returns new cleartext secret.
 */
export async function rotateWebhookSecret(merchantId: string): Promise<WebhookConfig> {
  return post<WebhookConfig>(
    `/webhooks/config/${encodeURIComponent(merchantId)}/rotate-secret`
  );
}

/**
 * Send a test webhook to the configured endpoint.
 */
export async function testWebhook(merchantId: string): Promise<WebhookTestResult> {
  return post<WebhookTestResult>(
    `/webhooks/config/${encodeURIComponent(merchantId)}/test`
  );
}
